"use client";

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, Crown, Zap, Users, Briefcase, CreditCard } from 'lucide-react';
import { paymentService } from '@/lib/payment-service';
import { showSuccess, showError } from '@/utils/toast';
import { useNavigate } from 'react-router-dom'; // Added for navigation

interface SubscriptionCardProps {
  subscription: any;
  onUpgrade?: (plan: 'pro' | 'enterprise') => void;
  currentPlan: string;
  billingInterval: 'month' | 'year';
  role?: string;
}

const planFeatures = {
  basic: [
    'Up to 5 projects',
    'Up to 2 agents (team members)',
    'Up to 5 clients',
    'Basic time tracking',
    'Email support',
  ],
  pro: [
    'Up to 20 projects',
    'Up to 50 agents (team members)',
    'Up to 500 clients',
    'Advanced analytics & reports',
    'Client portals with branding',
    'Priority email support',
    'Custom themes & integrations'
  ],
  enterprise: [
    'Unlimited projects, agents, & clients',
    'Everything in Pro',
    'Dedicated account manager',
    'Custom integrations (API/Slack)',
    '99.9% uptime SLA',
    'Advanced security & compliance'
  ],
};

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ 
  subscription, 
  onUpgrade, 
  currentPlan, 
  billingInterval, 
  role 
}) => {
  const navigate = useNavigate(); // Added for navigation
  const isActive = subscription?.status === 'active';
  const isPastDue = subscription?.status === 'past_due';
  const isTrialing = subscription?.status === 'trialing';
  const isCancelled = subscription?.status === 'cancelled';
  const isIncomplete = subscription?.status === 'incomplete';

  const currentPlanData = {
    name: currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1),
    features: planFeatures[currentPlan as keyof typeof planFeatures] || [],
  };

  const handleUpgradeClick = (plan: 'pro' | 'enterprise') => {
    if (onUpgrade) {
      onUpgrade(plan);
    } else {
      showError('Upgrade functionality not available. Please contact support.');
    }
  };

  // Helper to open external links safely (for signed PDF)
  const openExternalLink = (url: string) => {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card className={`w-full max-w-md ${isPastDue ? 'border-destructive/50 ring-2 ring-destructive/30' : ''}`}>
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-2xl font-bold">
            {currentPlanData.name} Plan
          </CardTitle>
          <Badge variant={isActive ? "default" : isPastDue ? "destructive" : "secondary"}>
            {subscription?.status || 'Inactive'}
            {isTrialing && <span className="ml-1 text-xs"> (Trial)</span>}
          </Badge>
        </div>
        <CardDescription className="text-lg font-semibold">
          {billingInterval === 'year' ? `£${subscription?.prorated_amount / 100 || 0}/yr` : `£${subscription?.prorated_amount / 100 || 0}/mo`}
        </CardDescription>
        {isPastDue && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription className="text-sm">
              Your subscription is past due. Please update your payment method to restore access.
            </AlertDescription>
          </Alert>
        )}
        {isCancelled && (
          <Alert variant="secondary" className="mt-2">
            <AlertDescription className="text-sm">
              Your subscription has been cancelled. Upgrade to restore your plan.
            </AlertDescription>
          </Alert>
        )}
        {isIncomplete && (
          <Alert variant="secondary" className="mt-2">
            <AlertDescription className="text-sm">
              Subscription setup incomplete. Complete payment to activate.
            </AlertDescription>
          </Alert>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-2">
          {currentPlanData.features.map((feature, index) => (
            <li key={index} className="flex items-center space-x-2">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col sm:flex-row gap-2">
          {isActive && !isCancelled && !isPastDue && (
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => handleUpgradeClick('pro')}>
              <CreditCard className="mr-2 h-4 w-4" />
              Upgrade to Pro
            </Button>
          )}
          {isActive && !isCancelled && !isPastDue && (
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => handleUpgradeClick('enterprise')}>
              <Crown className="mr-2 h-4 w-4" />
              Upgrade to Enterprise
            </Button>
          )}
          {(isCancelled || isPastDue || isIncomplete) && (
            <Button className="w-full sm:w-auto" onClick={() => handleUpgradeClick('pro')}>
              <Zap className="mr-2 h-4 w-4" />
              Restore Plan
            </Button>
          )}
        </div>
        {role === 'admin' && (
          <div className="text-xs text-muted-foreground mt-4">
            <p>Current period: {new Date(subscription?.current_period_start || Date.now()).toLocaleDateString()} - {new Date(subscription?.current_period_end || Date.now()).toLocaleDateString()}</p>
            {subscription?.grace_end && (
              <p className="text-destructive">Grace period ends: {new Date(subscription.grace_end).toLocaleDateString()}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionCard;